import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ABSENSI_STATUS, ABSENSI_LABEL } from "@/lib/constants";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const DAYS_SHORT = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];

function pad(n) { return String(n).padStart(2, "0"); }

/**
 * AbsensiCalendar
 *  records: [{ tanggal: 'YYYY-MM-DD', status: 'hadir'|'sakit'|'izin'|'alfa'|'bdr' }]
 */
export default function AbsensiCalendar({ records, title = "Kalender Absensi" }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0..11

  const statusByDate = useMemo(() => {
    const m = {};
    (records || []).forEach((r) => { m[r.tanggal] = r.status; });
    return m;
  }, [records]);

  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startWeekday = firstDay.getDay();

  const monthPrefix = `${year}-${pad(month + 1)}`;
  const monthRecords = (records || []).filter((r) => r.tanggal && r.tanggal.startsWith(monthPrefix));
  const statusCounts = ABSENSI_STATUS.reduce((acc, s) => { acc[s.v] = monthRecords.filter((r) => r.status === s.v).length; return acc; }, {});
  // Include legacy tidak_hadir in "Alpa" bucket for stats display
  statusCounts.alfa += monthRecords.filter((r) => r.status === "tidak_hadir").length;
  const hadirCount = statusCounts.hadir || 0;
  const totalMarked = Object.values(statusCounts).reduce((a, b) => a + b, 0);
  const pctHadir = totalMarked ? Math.round((hadirCount / totalMarked) * 100) : 0;

  const prev = () => { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); };
  const next = () => { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); };

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${pad(month + 1)}-${pad(d)}`;
    cells.push({ day: d, status: statusByDate[dateStr], dateStr });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="pp-card p-5 sm:p-6" data-testid="absensi-calendar">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{MONTHS[month]} {year}</p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={prev} className="w-9 h-9 rounded-lg border border-slate-300 hover:bg-slate-50 flex items-center justify-center" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth()); }} className="px-3 h-9 rounded-lg border border-slate-300 hover:bg-slate-50 text-xs font-medium" data-testid="cal-today">Hari ini</button>
          <button onClick={next} className="w-9 h-9 rounded-lg border border-slate-300 hover:bg-slate-50 flex items-center justify-center" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Stats — one card per status + persentase hadir */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-5">
        {ABSENSI_STATUS.map((s) => (
          <div key={s.v} className={`rounded-lg border px-2.5 py-2 ${s.cls}`}>
            <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{s.label}</div>
            <div className="text-xl font-bold mt-0.5" data-testid={`cal-${s.v}`}>{statusCounts[s.v]}</div>
          </div>
        ))}
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-2">
          <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">% Hadir</div>
          <div className="text-xl font-bold text-indigo-900 mt-0.5" data-testid="cal-pct">{pctHadir}%</div>
        </div>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS_SHORT.map((d) => (
          <div key={d} className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center py-1.5">{d}</div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c) return <div key={`e-${i}`} className="aspect-square" />;
          const isToday = c.dateStr === `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
          const legacyStatus = c.status === "tidak_hadir" ? "alfa" : c.status;
          const s = ABSENSI_STATUS.find((x) => x.v === legacyStatus);
          const bg = s ? s.cls : "bg-slate-50 text-slate-400";
          const badge = s ? s.badge : null;
          return (
            <div
              key={c.dateStr}
              className={`aspect-square rounded-lg border ${c.status ? "border" : "border-transparent"} ${bg} ${isToday ? "ring-2 ring-indigo-500 ring-offset-1" : ""} flex flex-col items-center justify-center text-xs relative`}
              data-testid={`cal-day-${c.dateStr}`}
              title={ABSENSI_LABEL[c.status] || "Belum diabsen"}
            >
              <span className="font-semibold">{c.day}</span>
              {badge && <span className="text-[9px] font-bold mt-0.5 opacity-70">{badge}</span>}
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex gap-3 text-xs text-slate-500 flex-wrap">
        {ABSENSI_STATUS.map((s) => (
          <div key={s.v} className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded border ${s.cls}`} /> {s.label}</div>
        ))}
        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-slate-100" /> Belum diabsen</div>
      </div>
    </div>
  );
}
